// Module rail (HANDOFF §1): 64 px, white, hairline end border; items 56 px
// (icon + 10/600 label; active = teal-light bg + teal-deep 10/700).
// compact (1024–1099): the menu folds into the rail — hover or press an entry
// to get its rows in a flyout. tablet (768–1023): icons only (label kept for
// screen readers + title). Footer: «مرخَّص لهذا الجهاز» (node mode, licensed)
// + the person's avatar menu.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { Avatar } from '@/components/Avatar';
import { signOut } from '@/lib/auth';
import type { LayoutClass } from '@/lib/layout';
import { useErp, useT } from './erp-context';
import { ModuleMenuItems } from './ModuleMenu';
import { labelOf, type RailEntry } from './gates';
import type { RailGroup } from './module-types';

export interface ModuleRailProps {
  rail: RailEntry[];
  active: RailGroup | null;
  layout: LayoutClass;
}

export function ModuleRail({ rail, active, layout }: ModuleRailProps) {
  const t = useT();
  const folded = layout === 'compact' || layout === 'tablet';
  const [flyout, setFlyout] = useState<{ group: RailGroup; top: number; start: number; by: 'hover' | 'press' } | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!flyout) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFlyout(null);
    const onDoc = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('.e-flyout') && !el.closest('.e-rail-item')) setFlyout(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [flyout]);

  const openFlyout = (group: RailGroup, el: HTMLElement, by: 'hover' | 'press') => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const r = el.getBoundingClientRect();
    const rtl = document.documentElement.dir !== 'ltr';
    setFlyout({ group, top: r.top, start: rtl ? window.innerWidth - r.left : r.right, by });
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setFlyout(null), 200);
  };

  const flyEntry = flyout ? rail.find((e) => e.group === flyout.group) ?? null : null;

  return (
    <aside className="e-rail" aria-label="الوحدات" data-testid="module-rail">
      <div className="e-rail-brand">
        <img src="/favicon-ehjizli.svg" alt="احجزلي" />
      </div>
      <nav className="e-rail-items">
        {rail.map((e) => {
          const Icon = e.icon;
          const label = labelOf(e, t);
          const isActive = e.group === active;
          const hasFlyout = folded && e.menu.length > 1;
          if (hasFlyout) {
            return (
              <button
                key={e.group}
                type="button"
                className="e-rail-item"
                data-active={isActive ? 'true' : undefined}
                aria-current={isActive ? 'page' : undefined}
                aria-expanded={flyout?.group === e.group}
                aria-haspopup="menu"
                title={label}
                data-testid={`rail-${e.group}`}
                onClick={(ev) => (flyout?.group === e.group && flyout.by === 'press' ? setFlyout(null) : openFlyout(e.group, ev.currentTarget, 'press'))}
                onMouseEnter={(ev) => flyout?.by !== 'press' && openFlyout(e.group, ev.currentTarget, 'hover')}
                onMouseLeave={() => flyout?.by !== 'press' && scheduleClose()}
              >
                <Icon aria-hidden="true" />
                <span className="e-rail-label">{label}</span>
              </button>
            );
          }
          return (
            <Link
              key={e.group}
              to={e.to}
              className="e-rail-item"
              aria-current={isActive ? 'page' : undefined}
              title={label}
              data-testid={`rail-${e.group}`}
            >
              <Icon aria-hidden="true" />
              <span className="e-rail-label">{label}</span>
            </Link>
          );
        })}
      </nav>
      <RailFooter />
      {flyout &&
        flyEntry &&
        createPortal(
          <div
            className="e-flyout"
            role="menu"
            data-testid="rail-flyout"
            style={{ top: flyout.top, insetInlineStart: flyout.start }}
            onMouseEnter={() => closeTimer.current && clearTimeout(closeTimer.current)}
            onMouseLeave={() => flyout.by !== 'press' && scheduleClose()}
          >
            <div className="e-flyout-title">{labelOf(flyEntry, t)}</div>
            <ModuleMenuItems entry={flyEntry} onNavigate={() => setFlyout(null)} />
          </div>,
          document.body,
        )}
    </aside>
  );
}

function RailFooter() {
  const { me, mode } = useErp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const licensed = mode === 'node' && !!me.node?.licensed;
  return (
    <div className="e-rail-footer">
      {licensed && (
        <span className="e-licensed" data-testid="rail-licensed">
          <ShieldCheck aria-hidden="true" />
          مرخَّص لهذا الجهاز
        </span>
      )}
      <div style={{ position: 'relative' }}>
        <button type="button" className="e-iconbtn" style={{ border: 0 }} aria-label={me.user.name} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <Avatar name={me.user.name} size={32} />
        </button>
        {open && (
          <div className="e-popover" style={{ insetInlineStart: 0, insetInlineEnd: 'auto', insetBlockStart: 'auto', insetBlockEnd: 0, position: 'absolute' }} role="menu">
            <div className="e-popover-item" style={{ cursor: 'default' }}>
              {me.user.name}
            </div>
            <button
              type="button"
              role="menuitem"
              className="e-popover-item"
              onClick={async () => {
                setOpen(false);
                await signOut();
                navigate('/auth/login', { replace: true });
              }}
            >
              تسجيل الخروج
              <LogOut aria-hidden="true" width={16} height={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
